import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react'

import { cn } from 'src/utils'

export interface PickListTrailing {
  /** The left-hand caption, e.g. "None of these". */
  caption: ReactNode
  /** The action, e.g. 'Add "acme" as typed'. */
  label: ReactNode
  onPick: () => void
}

interface PickListKeyboardParams<T> {
  items: T[]
  trailing?: PickListTrailing | null
  onPick: (item: T, index: number) => void
}

/**
 * Arrow keys and Enter for a PickList, from wherever the keyboard focus is:
 * the search input above it, or the list itself. Escape is left alone so the
 * drawer keeps its own meaning for it.
 */
export const usePickListKeyboard = <T,>({
  items,
  trailing,
  onPick,
}: PickListKeyboardParams<T>) => {
  const [highlighted, setHighlighted] = useState<number | null>(null)
  const total = items.length + (trailing ? 1 : 0)

  // A new list means the old highlight points at nothing
  useEffect(() => {
    setHighlighted(null)
  }, [items])

  const pick = useCallback(
    (index: number) => {
      if (index < items.length) onPick(items[index], index)
      else trailing?.onPick()
    },
    [items, onPick, trailing]
  )

  const onKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (total === 0) return
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault()
          setHighlighted((h) => (h === null || h >= total - 1 ? 0 : h + 1))
          break
        case 'ArrowUp':
          event.preventDefault()
          setHighlighted((h) => (h === null || h <= 0 ? total - 1 : h - 1))
          break
        case 'Home':
          event.preventDefault()
          setHighlighted(0)
          break
        case 'End':
          event.preventDefault()
          setHighlighted(total - 1)
          break
        case 'Enter':
          if (highlighted === null) return
          event.preventDefault()
          pick(highlighted)
          break
        default:
      }
    },
    [highlighted, pick, total]
  )

  return { highlighted, setHighlighted, onKeyDown, pick }
}

export interface PickListProps<T> {
  id: string
  items: T[]
  itemKey: (item: T, index: number) => string
  renderItem: (item: T, index: number) => ReactNode
  onPick: (item: T, index: number) => void
  trailing?: PickListTrailing | null
  /** Loader, "No suggestions found", an error — shown above the trailing row. */
  status?: ReactNode
  highlighted: number | null
  onHighlight: (index: number | null) => void
  /** When the list is not driven from an input it takes focus and keys itself. */
  onKeyDown?: (event: KeyboardEvent) => void
  'aria-label'?: string
}

export const pickListOptionId = (id: string, index: number) =>
  `${id}-opt-${index}`

/**
 * A plain list of things to pick from, in the look of the carrier dropdown
 * but sitting inline in the page, so a drawer can show it without a popover.
 */
export const PickList = <T,>({
  id,
  items,
  itemKey,
  renderItem,
  onPick,
  trailing,
  status,
  highlighted,
  onHighlight,
  onKeyDown,
  'aria-label': ariaLabel,
}: PickListProps<T>) => {
  const listRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (highlighted === null) return
    listRef.current
      ?.querySelector(`#${pickListOptionId(id, highlighted)}`)
      ?.scrollIntoView?.({ block: 'nearest' })
  }, [highlighted, id])

  const trailingIndex = items.length

  return (
    <div
      ref={listRef}
      id={id}
      role="listbox"
      aria-label={ariaLabel}
      tabIndex={onKeyDown ? 0 : -1}
      onKeyDown={onKeyDown}
      className="rounded border border-doubleNickel-gray-300 bg-white p-1 shadow-md outline-none focus-visible:ring-2 focus-visible:ring-doubleNickel-brand-200"
    >
      {items.map((item, index) => (
        <div
          key={itemKey(item, index)}
          id={pickListOptionId(id, index)}
          role="option"
          aria-selected={highlighted === index}
          tabIndex={-1}
          onKeyDown={onKeyDown}
          onMouseEnter={() => onHighlight(index)}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => onPick(item, index)}
          className={cn(
            'flex cursor-pointer items-start gap-3 rounded px-2 py-1.5',
            highlighted === index && 'bg-doubleNickel-gray-100'
          )}
        >
          {renderItem(item, index)}
        </div>
      ))}
      {status && (
        <div className="px-2 pb-1 pt-2 text-center text-xs text-doubleNickel-gray-700">
          {status}
        </div>
      )}
      {trailing && (
        <div
          id={pickListOptionId(id, trailingIndex)}
          role="option"
          aria-selected={highlighted === trailingIndex}
          tabIndex={-1}
          onKeyDown={onKeyDown}
          onMouseEnter={() => onHighlight(trailingIndex)}
          onMouseDown={(event) => event.preventDefault()}
          onClick={trailing.onPick}
          className={cn(
            'mt-1 flex cursor-pointer items-center justify-between gap-3 rounded border-t border-doubleNickel-gray-200 px-2 py-1.5',
            highlighted === trailingIndex && 'bg-doubleNickel-gray-100'
          )}
        >
          <span className="text-[11px] text-doubleNickel-gray-500">
            {trailing.caption}
          </span>
          <span className="text-xs font-medium text-doubleNickel-brand-500">
            {trailing.label}
          </span>
        </div>
      )}
    </div>
  )
}

export default PickList
